import { CharacterLimitExceeded, RequiredFieldNotice } from "constants/text";
import { NewOrdersDialogWrapper } from "../NewOrders.styles";
import { SaveDraftModalBody, SaveDraftModalButtons, SaveDraftModalHeader } from "./SaveDraft.styles";
import {
	ComeBackToOrderText,
	ComeBackToTemplateText,
	DraftNamePlaceholder,
	InputDraftTitleError,
	InputQuoteTitleError,
	InputTemplateTitleError,
	NewOrderQuoteNamePlaceholder,
	ReplacePreviouslySavedDraftText,
	ReplacePreviouslySavedTemplateText,
	SaveAndExitText,
	SaveDraftText,
	SaveTemplateText,
	SaveThisQuoteText,
	SubmitQuoteText,
	TemplateNamePlaceholder
} from "../constants";
import Close from "@mui/icons-material/Close";
import type { DialogProps } from "@mui/material";
import { Button, TextField } from "@mui/material";
import { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import HelperText from "components/Form/HelperText/HelperText";
import { v4 as uuidv4 } from "uuid";
import { models } from "types/api/viewModels.ts";
import { newOrderActions } from "features/reducers/newOrder/newOrder";
import { RootState } from "stores/application.store.tsx";
import { DraftType, DraftTypeType, OrderType } from "types/declarations/enums.ts";

interface SaveDraftModalProps {
	saveDraftModalIsOpen: boolean;
	closeModal: () => void;
	firstButtonText: string;
	secondButtonText: string;
	draftOrder?: models["DraftOrderViewModel"];
	draftType?: DraftTypeType;
	isExitingOrder?: boolean;
	isLoading?: boolean;
	closeOnSuccess?: boolean;
	onSubmit: (draft: models["DraftOrderViewModel"]) => Promise<void>;
	onSecondaryAction?: () => void;
}

const DRAFT_TITLE_MAX_CHAR = 50;

const templateDraftText: (draftType?: DraftTypeType) => {
	Name: DraftTypeType;
	InputTitleError: string;
	NamePlaceholder: string;
	SaveText: string;
	ReplacePreviouslySavedText: string;
	ComeBackToText: string;
} = (draftType) => {
	if (draftType === DraftType.Template) {
		return {
			Name: DraftType.Template,
			InputTitleError: InputTemplateTitleError,
			NamePlaceholder: TemplateNamePlaceholder,
			SaveText: SaveTemplateText,
			ReplacePreviouslySavedText: ReplacePreviouslySavedTemplateText,
			ComeBackToText: ComeBackToTemplateText
		};
	}
	if (draftType === DraftType.Quote) {
		return {
			Name: DraftType.Quote,
			InputTitleError: InputQuoteTitleError,
			NamePlaceholder: NewOrderQuoteNamePlaceholder,
			SaveText: SubmitQuoteText,
			ReplacePreviouslySavedText: SaveThisQuoteText,
			ComeBackToText: SaveThisQuoteText
		};
	}

	return {
		Name: DraftType.Draft,
		InputTitleError: InputDraftTitleError,
		NamePlaceholder: DraftNamePlaceholder,
		SaveText: SaveDraftText,
		ReplacePreviouslySavedText: ReplacePreviouslySavedDraftText,
		ComeBackToText: ComeBackToOrderText
	};
};

const SaveDraftModal = ({
	saveDraftModalIsOpen,
	closeModal,
	firstButtonText,
	secondButtonText,
	draftOrder,
	draftType = DraftType.Draft,
	isExitingOrder,
	isLoading = false,
	closeOnSuccess = true,
	onSubmit,
	onSecondaryAction
}: SaveDraftModalProps) => {
	const [draftTitle, setDraftTitle] = useState(draftOrder?.draftName ?? "");
	const [errorText, setErrorText] = useState<undefined | string>(undefined);
	const [draftTitleHasError, setDraftTitleHasError] = useState(false);
	const dispatch = useDispatch();
	const [isSavedDraft, setIsSavedDraft] = useState(draftOrder?.draftName && draftOrder.draftName !== "");
	const [isNewTemplate, setIsNewTemplate] = useState(false);
	const [hasSubmittedSuccessfully, setHasSubmittedSuccessfully] = useState(false);

	const handleCloseModal = useCallback(() => {
		if (isLoading) return;
		setErrorText(undefined);
		setDraftTitleHasError(false);
		setHasSubmittedSuccessfully(false);
		closeModal();
	}, [closeModal, isLoading]);

	const draftDetails = useSelector((state: RootState) => state.newOrder.draftDetails);

	const getDraftOrderId = () => {
		if (isNewTemplate) return uuidv4();

		const isTemplate = draftType === DraftType.Template;
		const isConvertingTemplateToQuote = draftOrder?.type === DraftType.Template && draftType === DraftType.Quote;

		if (isConvertingTemplateToQuote) {
			return uuidv4();
		}

		const isConvertingDraftToQuote = draftOrder?.type === DraftType.Draft && draftType === DraftType.Quote;

		if (isConvertingDraftToQuote) {
			return draftDetails?.currentDraftId ?? draftOrder?.draftOrderId ?? uuidv4();
		}

		if (isTemplate && draftDetails?.currentTemplateId) {
			return draftDetails.currentTemplateId;
		}

		if (!isTemplate && draftDetails?.currentDraftId) {
			return draftDetails.currentDraftId;
		}

		if (draftOrder?.draftOrderId && draftOrder.type === draftType) {
			return draftOrder.draftOrderId;
		}

		return uuidv4();
	};

	const submitDraft = async () => {
		const draftOrderId = getDraftOrderId();
		const isConvertingTemplateToQuote = draftOrder?.type === DraftType.Template && draftType === DraftType.Quote;

		const draft: models["DraftOrderViewModel"] = {
			...draftOrder,
			draftOrderId: draftOrderId,
			tandemOrderId: isConvertingTemplateToQuote ? draftOrderId : draftOrder?.tandemOrderId,
			draftName: draftTitle.trim(),
			type: templateDraftText(draftType).Name,
			orderType: draftType === DraftType.Quote ? OrderType.QUOTE_ORDER : draftOrder?.orderType
		};

		dispatch(
			newOrderActions.setDraftId({ currentDraftId: draftOrderId, isTemplate: draftType === DraftType.Template })
		);
		dispatch(newOrderActions.overwriteDraftOrder(draft));
		dispatch(newOrderActions.clearDraftError());

		await onSubmit(draft);
		setIsSavedDraft(true);
		setHasSubmittedSuccessfully(true);
		if (closeOnSuccess) {
			handleCloseModal();
		}
	};

	const checkForErrors = async () => {
		if (draftTitle.length > DRAFT_TITLE_MAX_CHAR) return;

		if (draftTitle.trim() === "") {
			setDraftTitleHasError(true);
			setErrorText(templateDraftText(draftType).InputTitleError);
		} else {
			try {
				await submitDraft();
			} catch {
				// Parent onSubmit handlers surface save failures through existing error state/snackbars.
			}
		}
	};

	const draftTitleHelperText = () => {
		if (draftTitleHasError) return errorText;
		if (draftTitle.length > DRAFT_TITLE_MAX_CHAR) return CharacterLimitExceeded;
		return "";
	};

	const handleDialogClose: DialogProps["onClose"] = (_event, reason) => {
		if (isInteractionDisabled) return;
		if (reason === "backdropClick") return;
		handleCloseModal();
	};

	const texts = templateDraftText(draftType);
	const subheaderText = isSavedDraft ? texts.ReplacePreviouslySavedText : texts.ComeBackToText;
	const isInteractionDisabled = isLoading || hasSubmittedSuccessfully;

	return (
		<NewOrdersDialogWrapper
			open={saveDraftModalIsOpen}
			onClose={handleDialogClose}
			disableEscapeKeyDown={isInteractionDisabled}
			data-testid="save-draft-modal"
		>
			<SaveDraftModalHeader>
				<div>
					<h6>{isExitingOrder ? SaveAndExitText : templateDraftText(draftType).SaveText}</h6>
					<p>{subheaderText}</p>
				</div>
				<Close
					onClick={isInteractionDisabled ? undefined : handleCloseModal}
					sx={{
						opacity: isInteractionDisabled ? 0.5 : 1,
						cursor: isInteractionDisabled ? "default" : "pointer"
					}}
				/>
			</SaveDraftModalHeader>
			<SaveDraftModalBody isError={draftTitleHasError || draftTitle.length > DRAFT_TITLE_MAX_CHAR}>
				<p>{RequiredFieldNotice}</p>
				<TextField
					label={templateDraftText(draftType).NamePlaceholder}
					value={draftTitle}
					disabled={isInteractionDisabled}
					data-testid="save-draft-input"
					onChange={(e) => {
						setDraftTitleHasError(false);
						setDraftTitle(e.target.value);
						setIsNewTemplate(
							e.target.value.trim() !== draftOrder?.draftName && draftType === DraftType.Template
						);
					}}
					sx={{ marginBottom: "4px" }}
				/>
				<HelperText
					isError={draftTitle.length > DRAFT_TITLE_MAX_CHAR || draftTitleHasError}
					text={draftTitleHelperText()}
					characterLimit={{
						currentLength: draftTitle.length,
						limit: DRAFT_TITLE_MAX_CHAR,
						isLimitError: draftTitle.length > DRAFT_TITLE_MAX_CHAR
					}}
				/>
				<SaveDraftModalButtons>
					<Button
						variant="outlined"
						disabled={isInteractionDisabled}
						onClick={onSecondaryAction ?? handleCloseModal}
					>
						{secondButtonText}
					</Button>
					<Button
						variant="contained"
						disabled={isInteractionDisabled || draftTitle.length > DRAFT_TITLE_MAX_CHAR}
						onClick={checkForErrors}
					>
						{firstButtonText}
					</Button>
				</SaveDraftModalButtons>
			</SaveDraftModalBody>
		</NewOrdersDialogWrapper>
	);
};

export default SaveDraftModal;
